import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Users2, UserX, Clock, Headphones, ShieldAlert, AlertTriangle, ArrowUpRight, Eye, Pencil, UserPlus, FileSearch, Loader2, Target, Check, X, Building2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { Sel, ExportBtn, downloadCsv, deDT, Pagination, Modal, Drawer, TEAM_ROLE_CHIP, teamRoleLabel } from "./shared";
import { Avatar, avatarRing, DrawerSection, KV } from "./ui";

const ASSIGN_STATUS = {
  vollstaendig: { dot: "bg-[#A3E635]", chip: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]", label: "Vollständig" },
  teilweise: { dot: "bg-amber-400", chip: "bg-amber-500/12 border-amber-500/30 text-amber-300", label: "Teilweise" },
  offen: { dot: "bg-sky-400", chip: "bg-sky-500/12 border-sky-500/30 text-sky-300", label: "Offen" },
  kritisch: { dot: "bg-red-500", chip: "bg-red-500/12 border-red-500/30 text-red-300", label: "Kritisch" },
};
const HEALTH = {
  good: { dot: "bg-[#A3E635]", txt: "text-[#A3E635]", label: "Gut" },
  warning: { dot: "bg-amber-400", txt: "text-amber-400", label: "Achtung" },
  critical: { dot: "bg-red-500", txt: "text-red-400", label: "Kritisch" },
};
const ACCENT = {
  lime: { txt: "text-[#A3E635]", bg: "bg-[#A3E635]/12", bar: "bg-[#A3E635]" },
  red: { txt: "text-red-400", bg: "bg-red-500/12", bar: "bg-red-500" },
  amber: { txt: "text-amber-400", bg: "bg-amber-500/12", bar: "bg-amber-400" },
  sky: { txt: "text-sky-300", bg: "bg-sky-500/12", bar: "bg-sky-400" },
  gray: { txt: "text-gray-300", bg: "bg-white/5", bar: "bg-gray-500" },
};
const KPI_CFG = [
  { key: "vollstaendig", label: "Kunden mit vollständiger Zuweisung", icon: Users2, accent: "lime" },
  { key: "ohne_owner", label: "Ohne Hauptverantwortung", icon: UserX, accent: "red" },
  { key: "offen", label: "Offene Zuweisungen", icon: Clock, accent: "gray" },
  { key: "support_ohne_owner", label: "Nur Support ohne Owner", icon: Headphones, accent: "sky" },
  { key: "kritisch", label: "Kritische Fälle", icon: ShieldAlert, accent: "red" },
  { key: "handlungsbedarf", label: "Kunden mit Handlungsbedarf", icon: AlertTriangle, accent: "amber" },
];

function ActionPill({ icon: Icon, label, onClick, testid, title }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title || label}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-colors whitespace-nowrap">
      <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
    </button>
  );
}

function MiniUser({ u, sub, t }) {
  if (!u) return <span className="text-xs text-gray-600">—</span>;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar name={u.name} accent={avatarRing({ role: u.team_role === "super_admin" ? "super_admin" : "team_member", team_role: u.team_role })} size="sm" />
      <div className="min-w-0">
        <div className="text-xs font-semibold truncate max-w-[130px]">{u.name}</div>
        <div className="text-[10px] text-gray-500 truncate max-w-[130px]">{t(sub)}</div>
      </div>
    </div>
  );
}

function KpiCard({ cfg, data, t }) {
  const a = ACCENT[cfg.accent] || ACCENT.gray;
  const pct = data.total ? Math.round((data.value / data.total) * 100) : 0;
  const Icon = cfg.icon;
  return (
    <Card className="p-4" data-testid={`assign-kpi-${cfg.key}`}>
      <div className="flex items-start gap-2.5 mb-2">
        <div className={`w-8 h-8 rounded-lg ${a.bg} flex items-center justify-center shrink-0`}><Icon className={`w-4 h-4 ${a.txt}`} /></div>
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold leading-tight pt-0.5">{t(cfg.label)}</span>
      </div>
      <div className="flex items-baseline gap-1.5 mb-1.5">
        <span className={`text-3xl font-black ${a.txt}`} data-testid={`assign-kpi-${cfg.key}-value`}>{data.value}</span>
        <span className="text-xs text-gray-500">{t("von")} {data.total}</span>
      </div>
      <div className="text-[11px] mb-2 flex items-center gap-1">
        {data.trend > 0
          ? <span className="text-[#A3E635] inline-flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> +{data.trend} {t("seit letzter Woche")}</span>
          : <span className="text-gray-500">{t("Keine Veränderung")}</span>}
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden"><div className={`h-full ${a.bar} rounded-full transition-all`} style={{ width: `${pct}%` }} /></div>
    </Card>
  );
}

export function AssignmentsTab({ ctx }) {
  const { t, teamUsers, clients, lastRefreshed } = ctx;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fPackage, setFPackage] = useState("all");
  const [fHealth, setFHealth] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [tq, setTq] = useState("");
  const [tRole, setTRole] = useState("all");
  const [detail, setDetail] = useState(null);
  const [editor, setEditor] = useState(null);
  const [audit, setAudit] = useState(null);
  const [teamEdit, setTeamEdit] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/customer-assignments"); setData(data); setErr(false); }
    catch (e) { setErr(true); }
  }, []);
  useEffect(() => { load(); }, [load, lastRefreshed]);

  const members = useMemo(() => (teamUsers || []).filter((u) => u.role === "team_member"), [teamUsers]);
  const packages = useMemo(() => Array.from(new Set((data?.customers || []).map((c) => c.package).filter(Boolean))).sort(), [data]);
  const wrap = (set) => (v) => { set(v); setPage(1); };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.customers || []).filter((c) => {
      if (s && !c.company_name.toLowerCase().includes(s) && !(c.customer_code || "").toLowerCase().includes(s) && !(c.primary_owner?.name || "").toLowerCase().includes(s)) return false;
      if (fStatus !== "all" && c.status !== fStatus) return false;
      if (fPackage !== "all" && c.package !== fPackage) return false;
      if (fHealth !== "all" && c.health !== fHealth) return false;
      return true;
    });
  }, [data, q, fStatus, fPackage, fHealth]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const cur = Math.min(page, pages);
  const pageRows = filtered.slice((cur - 1) * perPage, cur * perPage);

  const teamFiltered = useMemo(() => {
    const s = tq.trim().toLowerCase();
    return (data?.team || []).filter((m) => {
      if (s && !(m.name || "").toLowerCase().includes(s)) return false;
      if (tRole !== "all" && m.team_role !== tRole) return false;
      return true;
    });
  }, [data, tq, tRole]);

  const exportCsv = () => downloadCsv("kunden-zuweisungen.csv", [
    { label: "Kunde", value: (c) => c.company_name }, { label: "Code", value: (c) => c.customer_code },
    { label: "Status", value: (c) => t(ASSIGN_STATUS[c.status]?.label || c.status) },
    { label: "Hauptverantwortlich", value: (c) => c.primary_owner?.name || "" },
    { label: "Support", value: (c) => c.support?.name || "" }, { label: "Projektleitung", value: (c) => c.project_lead?.name || "" },
    { label: "Teammitglieder", value: (c) => c.member_count }, { label: "Paket", value: (c) => c.package },
    { label: "Health", value: (c) => t(HEALTH[c.health]?.label || c.health) },
  ], filtered);

  if (!data && !err) return <Card className="p-10 text-center" data-testid="sec-panel-assign"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></Card>;
  if (err) return <Card className="p-8 text-center text-sm text-red-300" data-testid="sec-panel-assign">{t("Daten konnten nicht geladen werden.")} <button onClick={load} className="underline ml-2">{t("Erneut versuchen")}</button></Card>;

  return (
    <div className="space-y-5" data-testid="sec-panel-assign">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="assign-kpis">
        {KPI_CFG.map((cfg) => <KpiCard key={cfg.key} cfg={cfg} data={data.kpis[cfg.key] || { value: 0, total: data.total, trend: 0 }} t={t} />)}
      </div>

      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => wrap(setQ)(e.target.value)} data-testid="assign-search" placeholder={t("Kunden suchen…")} className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs focus:border-[#A3E635] outline-none" />
          </div>
          <Sel value={fStatus} onChange={wrap(setFStatus)} testid="assign-f-status" options={[{ value: "all", label: t("Status: Alle") }, ...Object.keys(ASSIGN_STATUS).map((k) => ({ value: k, label: t(ASSIGN_STATUS[k].label) }))]} />
          <Sel value={fPackage} onChange={wrap(setFPackage)} testid="assign-f-package" options={[{ value: "all", label: t("Paket: Alle") }, ...packages.map((p) => ({ value: p, label: p }))]} />
          <Sel value={fHealth} onChange={wrap(setFHealth)} testid="assign-f-health" options={[{ value: "all", label: t("Health: Alle") }, ...Object.keys(HEALTH).map((k) => ({ value: k, label: t(HEALTH[k].label) }))]} />
          <ExportBtn onClick={exportCsv} label={t("Exportieren")} disabled={total === 0} title={t("Als CSV exportieren")} testid="assign-export" />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="assign-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
              <th className="text-left font-semibold px-5 py-3">{t("Kunde")}</th>
              <th className="text-left font-semibold px-3 py-3">{t("Zuweisungsstatus")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">{t("Hauptverantwortlich")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden xl:table-cell">{t("Support")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden xl:table-cell">{t("Projektleitung")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">{t("Zugewiesene Teammitglieder")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">{t("Paket")}</th>
              <th className="text-left font-semibold px-3 py-3">{t("Health")}</th>
              <th className="text-right font-semibold px-5 py-3">{t("Aktionen")}</th>
            </tr></thead>
            <tbody>
              {pageRows.length === 0 && <tr><td colSpan={9} className="px-5 py-8 text-center text-gray-500 text-sm">{t("Keine Kunden gefunden.")}</td></tr>}
              {pageRows.map((c) => { const s = ASSIGN_STATUS[c.status] || ASSIGN_STATUS.kritisch; const h = HEALTH[c.health] || HEALTH.good; return (
                <tr key={c.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015] align-middle" data-testid={`assign-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={c.company_name} accent="sky" size="sm" />
                      <div className="min-w-0"><div className="font-semibold truncate max-w-[170px]">{c.company_name}</div><div className="text-[11px] text-gray-500">{c.customer_code}</div></div>
                    </div>
                  </td>
                  <td className="px-3 py-3"><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.chip}`} data-testid={`assign-status-${c.id}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{t(s.label)}</span></td>
                  <td className="px-3 py-3 hidden lg:table-cell"><MiniUser u={c.primary_owner} sub="Account Manager" t={t} /></td>
                  <td className="px-3 py-3 hidden xl:table-cell"><MiniUser u={c.support} sub="Support Specialist" t={t} /></td>
                  <td className="px-3 py-3 hidden xl:table-cell">{c.project_lead ? <span className="text-xs font-semibold">{c.project_lead.name}</span> : <span className="text-xs text-gray-600">—</span>}</td>
                  <td className="px-3 py-3 hidden lg:table-cell">
                    {c.members.length ? <div className="flex items-center gap-1.5"><div className="flex -space-x-2">{c.members.slice(0, 3).map((m) => <Avatar key={m.id} name={m.name} accent={avatarRing({ role: "team_member", team_role: m.team_role })} size="sm" />)}</div>{c.members.length > 3 && <span className="text-[11px] text-gray-400">+{c.members.length - 3}</span>}</div>
                      : <span className="text-xs text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell">{c.package ? <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded bg-white/5 border border-white/10 text-gray-300">{c.package}</span> : <span className="text-xs text-gray-600">—</span>}</td>
                  <td className="px-3 py-3"><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${h.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${h.dot}`} />{t(h.label)}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <ActionPill icon={Eye} label={t("Anzeigen")} onClick={() => setDetail(c)} testid={`assign-view-${c.id}`} />
                      <ActionPill icon={Pencil} label={t("Bearbeiten")} onClick={() => setEditor(c)} testid={`assign-edit-${c.id}`} />
                      <ActionPill icon={UserPlus} label={t("Zuweisen")} onClick={() => setEditor(c)} testid={`assign-assign-${c.id}`} />
                      <ActionPill icon={FileSearch} label={t("Audit")} onClick={() => setAudit(c)} testid={`assign-audit-${c.id}`} />
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        <Pagination page={cur} setPage={setPage} total={total} perPage={perPage} setPerPage={setPerPage} t={t} testid="assign-pagination" />
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="assign-team-section">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 font-bold text-sm tracking-tight"><Target className="w-4 h-4 text-[#A3E635]" /> {t("Team-Zuweisungen (KEWAG-Team)")} <span className="text-xs text-gray-500 font-normal">{(data.team || []).length}</span></div>
          <div className="flex items-center gap-2">
            <div className="relative"><Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" /><input value={tq} onChange={(e) => setTq(e.target.value)} data-testid="team-search" placeholder={t("Teammitglied suchen…")} className="bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs w-48 focus:border-[#A3E635] outline-none" /></div>
            <Sel value={tRole} onChange={setTRole} testid="team-f-role" options={[{ value: "all", label: t("Rolle: Alle") }, ...Array.from(new Set((data.team || []).map((m) => m.team_role).filter(Boolean))).map((r) => ({ value: r, label: teamRoleLabel(ctx.matrix, r) }))]} />
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-px bg-white/5">
          {teamFiltered.length === 0 && <div className="p-6 text-gray-500 text-sm bg-[#121217] col-span-full">{t("Keine Teammitglieder gefunden.")}</div>}
          {teamFiltered.map((m) => {
            const high = m.workload_percent >= 80;
            return (
              <div key={m.id} className="bg-[#121217] p-4 flex items-center gap-4" data-testid={`team-card-${m.id}`}>
                <Avatar name={m.name} accent={avatarRing({ role: "team_member", team_role: m.team_role })} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{m.name}</div>
                  <span className={`mt-0.5 inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TEAM_ROLE_CHIP[m.team_role] || "bg-white/5 border-white/10 text-gray-400"}`}>{teamRoleLabel(ctx.matrix, m.team_role)}</span>
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Zugewiesene Kunden")}</div>
                    {m.customers.length ? <div className="flex items-center gap-1 flex-wrap">{m.customers.slice(0, 3).map((c) => <span key={c.id} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-300 truncate max-w-[110px]">{c.name}</span>)}{m.customers.length > 3 && <span className="text-[10px] text-gray-500">+{m.customers.length - 3}</span>}</div>
                      : <span className="text-[11px] text-gray-600">{t("Keine Kunden")}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 w-28">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{t("Auslastung")}</div>
                  <div className={`text-sm font-bold ${high ? "text-red-400" : "text-[#A3E635]"}`} data-testid={`team-workload-${m.id}`}>{m.workload_percent}%</div>
                  <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden mt-1"><div className={`h-full rounded-full ${high ? "bg-red-500" : "bg-[#A3E635]"}`} style={{ width: `${m.workload_percent}%` }} /></div>
                  <div className="text-[9px] uppercase tracking-wider text-gray-500 mt-2">{t("Letzte Aktivität")}</div>
                  <div className="text-[10px] text-gray-400">{deDT(m.last_activity) || t("Nie")}</div>
                </div>
                <button onClick={() => setTeamEdit(m)} data-testid={`team-edit-${m.id}`} className="bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold py-1.5 px-3 rounded-lg flex items-center gap-1.5 transition-colors shrink-0 self-start"><Pencil className="w-3.5 h-3.5" /> {t("Zuweisung bearbeiten")}</button>
              </div>
            );
          })}
        </div>
      </Card>

      {detail && <DetailDrawer c={detail} t={t} onClose={() => setDetail(null)} onEdit={() => { setEditor(detail); setDetail(null); }} onAudit={() => { setAudit(detail); setDetail(null); }} />}
      {editor && <AssignEditor c={editor} members={members} t={t} onClose={() => setEditor(null)} onDone={() => { setEditor(null); load(); }} />}
      {audit && <AuditDrawer c={audit} t={t} onClose={() => setAudit(null)} />}
      {teamEdit && <TeamEditor m={teamEdit} clients={clients} t={t} onClose={() => setTeamEdit(null)} onDone={() => { setTeamEdit(null); load(); }} />}
    </div>
  );
}

function DetailDrawer({ c, t, onClose, onEdit, onAudit }) {
  const s = ASSIGN_STATUS[c.status] || ASSIGN_STATUS.kritisch;
  const h = HEALTH[c.health] || HEALTH.good;
  return (
    <Drawer title={t("Kunden-Zuweisung")} onClose={onClose} testid="assign-detail-drawer">
      <div className="mb-5 flex items-center gap-3">
        <Avatar name={c.company_name} accent="sky" size="lg" />
        <div className="min-w-0"><div className="font-bold text-base truncate">{c.company_name}</div><div className="text-xs text-gray-500">{c.customer_code} · {c.package || "—"}</div>
          <span className={`mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{t(s.label)}</span></div>
      </div>
      <DrawerSection title={t("Übersicht")}>
        <KV label={t("Zuweisungsstatus")} value={t(s.label)} />
        <KV label={t("Health")} value={<span className={`inline-flex items-center gap-1.5 ${h.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${h.dot}`} />{t(h.label)}</span>} />
        <KV label={t("Paket")} value={c.package || "—"} />
        <KV label={t("Branche")} value={c.industry || "—"} />
      </DrawerSection>
      <DrawerSection title={t("Verantwortlichkeiten")}>
        <KV label={t("Hauptverantwortlich")} value={c.primary_owner?.name || "—"} accent={c.primary_owner ? "" : "text-red-400"} />
        <KV label={t("Support")} value={c.support?.name || "—"} />
        <KV label={t("Projektleitung")} value={c.project_lead?.name || "—"} />
      </DrawerSection>
      <DrawerSection title={`${t("Zugewiesene Teammitglieder")} (${c.member_count})`}>
        {c.members.length ? <div className="flex flex-wrap gap-1.5 py-1">{c.members.map((m) => <span key={m.id} className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-gray-300">{m.name}</span>)}</div> : <span className="text-[11px] text-gray-600">{t("Keine Mitglieder zugewiesen")}</span>}
      </DrawerSection>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={onEdit} data-testid="assign-detail-edit" className="bg-[#A3E635] text-black font-bold py-2 rounded-lg text-sm flex items-center justify-center gap-2"><Pencil className="w-4 h-4" /> {t("Bearbeiten")}</button>
        <button onClick={onAudit} data-testid="assign-detail-audit" className="bg-white/5 hover:bg-white/10 border border-white/10 font-semibold py-2 rounded-lg text-sm flex items-center justify-center gap-2"><FileSearch className="w-4 h-4 text-[#A3E635]" /> {t("Audit")}</button>
      </div>
    </Drawer>
  );
}

function MemberSelect({ label, value, onChange, members, t, testid, allowEmpty = true }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testid} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#A3E635] outline-none">
        {allowEmpty && <option value="">{t("— Niemand —")}</option>}
        {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>
  );
}

function AssignEditor({ c, members, t, onClose, onDone }) {
  const [primary, setPrimary] = useState(c.primary_owner?.id || null);
  const [support, setSupport] = useState(c.support?.id || null);
  const [lead, setLead] = useState(c.project_lead?.id || null);
  const roleIds = useMemo(() => new Set([c.primary_owner?.id, c.support?.id, c.project_lead?.id]), [c]);
  const [memberIds, setMemberIds] = useState(() => c.members.filter((m) => !roleIds.has(m.id)).map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setMemberIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/customer-assignments/customer/${c.id}`, { primary_owner_id: primary, support_owner_id: support, project_lead_id: lead, member_ids: memberIds });
      toast.success(t("Zuweisung gespeichert"));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Zuweisung bearbeiten")} onClose={onClose} testid="assign-editor-modal" wide>
      <p className="text-sm text-gray-400 mb-4">{c.company_name} · <span className="text-gray-500">{c.customer_code}</span></p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <MemberSelect label={t("Hauptverantwortlich")} value={primary} onChange={setPrimary} members={members} t={t} testid="assign-sel-primary" />
        <MemberSelect label={t("Support")} value={support} onChange={setSupport} members={members} t={t} testid="assign-sel-support" />
        <MemberSelect label={t("Projektleitung")} value={lead} onChange={setLead} members={members} t={t} testid="assign-sel-lead" />
      </div>
      <div className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid="assign-members">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{t("Weitere Teammitglieder")} ({memberIds.length})</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[34vh] overflow-y-auto">
          {members.map((m) => (
            <label key={m.id} data-testid={`assign-member-${m.id}`} className="flex items-center gap-2.5 py-1.5 px-1.5 rounded cursor-pointer hover:bg-white/[0.03]">
              <input type="checkbox" checked={memberIds.includes(m.id)} onChange={() => toggle(m.id)} className="accent-[#A3E635] w-4 h-4" />
              <span className="text-sm truncate">{m.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={save} disabled={busy} data-testid="assign-editor-save" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("Speichern")}</button>
      </div>
    </Modal>
  );
}

function TeamEditor({ m, clients, t, onClose, onDone }) {
  const extClients = useMemo(() => (clients || []).filter((c) => (c.client_type || "paying") !== "internal").sort((a, b) => (a.company_name || "").localeCompare(b.company_name || "")), [clients]);
  const [ids, setIds] = useState(() => m.customers.map((c) => c.id));
  const [workload, setWorkload] = useState(m.workload_percent || 0);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const toggle = (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const shown = extClients.filter((c) => !q || (c.company_name || "").toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/customer-assignments/team/${m.id}`, { customer_ids: ids, workload_percent: Number(workload) }); toast.success(t("Team-Zuweisung gespeichert")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Team-Zuweisung bearbeiten")} onClose={onClose} testid="team-editor-modal" wide>
      <p className="text-sm text-gray-400 mb-4">{m.name}</p>
      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">{t("Auslastung")}: <span className="text-[#A3E635]">{workload}%</span></label>
        <input type="range" min="0" max="100" value={workload} onChange={(e) => setWorkload(e.target.value)} data-testid="team-workload-slider" className="w-full accent-[#A3E635]" />
      </div>
      <div className="relative mb-2"><Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Kunden suchen…")} className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs focus:border-[#A3E635] outline-none" /></div>
      <div className="rounded-lg border border-white/10 bg-black/20 p-2 max-h-[40vh] overflow-y-auto" data-testid="team-customers">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 px-1 mb-1">{t("Zugewiesene Kunden")} ({ids.length})</div>
        {shown.map((c) => (
          <label key={c.id} data-testid={`team-customer-${c.id}`} className="flex items-center gap-2.5 py-1.5 px-1.5 rounded cursor-pointer hover:bg-white/[0.03]">
            <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} className="accent-[#A3E635] w-4 h-4" />
            <Building2 className="w-3.5 h-3.5 text-sky-300" /><span className="text-sm truncate">{c.company_name}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={save} disabled={busy} data-testid="team-editor-save" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("Speichern")}</button>
      </div>
    </Modal>
  );
}

function AuditDrawer({ c, t, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => { try { const { data } = await api.get(`/admin/customer-assignments/customer/${c.id}/audit`); setItems(data.items || []); } catch (e) { setItems([]); } })();
  }, [c.id]);
  return (
    <Drawer title={`${t("Audit-Log")} · ${c.company_name}`} onClose={onClose} testid="assign-audit-drawer">
      {items === null && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></div>}
      {items !== null && items.length === 0 && <div className="py-8 text-center text-sm text-gray-500" data-testid="assign-audit-empty">{t("Noch keine Audit-Einträge für diesen Kunden.")}</div>}
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid={`assign-audit-item-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-gray-200">{it.action}</span>
              <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded ${it.type === "security" ? "bg-amber-500/12 text-amber-300" : "bg-sky-500/12 text-sky-300"}`}>{it.type === "security" ? t("Sicherheit") : t("Aktivität")}</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">{it.actor || "—"} · {deDT(it.created_at) || ""}</div>
            {it.detail && <div className="text-[11px] text-gray-400 mt-1">{it.detail}</div>}
          </div>
        ))}
      </div>
    </Drawer>
  );
}
